import React, {useContext, useEffect, useState} from "react"
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import {RouteDetailContext} from "./context/RouteDetailContext";
import Link from "@amzn/meridian/link";
import {FormattedMessage} from "react-intl";
import {CapabilityContext} from "../global/context/CapabilityContext";
import {
    ASSIGNABLE, GROCERY_PICK,
    IN_PROGRESS,
    NOT_READY,
    PICK_IN_PROGRESS, PICK_PENDING, PRE_STAGED,
    PRIORITIZE_ENABLED_STATES,
    SIDELINE_ENABLED_STATES,
    SSD_PICK, STAGE_IN_PROGRESS, STAGE_PENDING,
    UNASSIGN_STAGER, UNASSIGN_PICKER
} from "../util/EntityStatusManager";
import Column from "@amzn/meridian/column";
import Heading from "@amzn/meridian/heading";
import {
    getFCLMLink,
    isARorSSD,
    isDynamicRoute,
    isReversePicklist,
    parseURLParam,
    isPicklistSplittingComplete,
    showRouteStatus
} from "../util/Util";
import Button from "@amzn/meridian/button";
import {AlertContext} from "../alert/context/AlertContext";
import {getStationCode} from "../network/NetworkUtil";
import CancelConfirmModal from "../commonModals/CancelConfirmModal";
import {AR, FEATURES, NOT_IN_CART, TOP_PRIORITY} from "../util/Constants";
import {isTCPAuthorizedUser} from "../../helper/PickVisibilityAuthorizationHelper";
import {getFeaturesFromParent} from "../handler/ParentDataHandler";

const RouteMetadataV2 = (props) => {
    const {viewModelRD, dispatchRD} = useContext(RouteDetailContext)
    const {configViewModel} = useContext(CapabilityContext)
    const {alertDispatch} = useContext(AlertContext)
    const [openForceAssignModal, setOpenForceAssignModal] = useState(false)
    const isNodeARorSSD = isARorSSD(configViewModel.capability);
    const {isAuthorizedUser, isTCPAuthorizationCheckEnabled} = viewModelRD;
    const isRouteDetailsTabViewEnabled = configViewModel && configViewModel.stationConfig && configViewModel.stationConfig.routeDetailsTabViewEnabled;

    function hasGroceryPicklists(viewModel) {
        return viewModel.picklists && viewModel.picklists.some(data =>
            data.workGroupSequenceList &&
                data.workGroupSequenceList.some(workGroup => workGroup.workGroupType === GROCERY_PICK)
        );
    }

    function getPicklistToUnassign(route, viewModel) {
        // If not Grocery Workflow, just return the routeId as per previous workflow.
        if (!hasGroceryPicklists(viewModel)) {
            return route.routeId;
        }

        if (isRouteDetailsTabViewEnabled) {
            const inProgressStatuses = [PICK_PENDING, PICK_IN_PROGRESS, STAGE_PENDING, STAGE_IN_PROGRESS, IN_PROGRESS];
            // Find core and grocery picklists with in-progress status
            const findPicklist = (workGroupType) => viewModel.picklists && viewModel.picklists.find(picklist =>
                inProgressStatuses.includes(picklist.status) &&
                picklist.workGroupSequenceList && picklist.workGroupSequenceList.some(workGroup => workGroup.workGroupType === workGroupType)
            );

            const corePicklist = findPicklist(SSD_PICK);
            const groceryPicklist = findPicklist(GROCERY_PICK);
            // If both or only core picklist have in-progress status, return core picklist ID
            if (corePicklist) {
                return corePicklist.picklistId;
            }
            // if only grocery picklist has in-progress status, return grocery picklist ID
            if (groceryPicklist) {
                return groceryPicklist.picklistId;
            }
            // If no picklist has in-progress status, return route ID
            return route.routeId;
        }

        let inProgressPicklist = viewModel.picklists
            && viewModel.picklists.find(picklist => picklist.status === IN_PROGRESS);
        return inProgressPicklist ? inProgressPicklist.picklistId : route.routeId;
    }

    function getUnassignParams(route, viewModel) {
        const IN_PROGRESS_STATUSES = [PICK_PENDING, PICK_IN_PROGRESS, STAGE_PENDING, STAGE_IN_PROGRESS, IN_PROGRESS, PRE_STAGED];
        const PICK_STATUSES = [PICK_PENDING, PICK_IN_PROGRESS];

        // Non-grocery workflow: default to stager unassignment
        if (!hasGroceryPicklists(viewModel)) {
            return {
                operationType: UNASSIGN_STAGER,
                routeId: route.routeId,
                picklistId: route.routeId
            };
        }

        // Find in-progress picklist by work group type
        const findInProgressPicklist = (workGroupType) => viewModel.picklists && viewModel.picklists.find(picklist =>
            IN_PROGRESS_STATUSES.includes(picklist.status) &&
            picklist.workGroupSequenceList && picklist.workGroupSequenceList.some(workGroup => workGroup.workGroupType === workGroupType)
        );

        const corePicklist = findInProgressPicklist(SSD_PICK);
        const groceryPicklist = findInProgressPicklist(GROCERY_PICK);

        // Unassign Priority: Core > Grocery > Default picklistId (same as routeId)
        const inProgressPicklist = corePicklist || groceryPicklist;

        if (!inProgressPicklist) {
            return {
                operationType: UNASSIGN_STAGER,
                routeId: route.routeId,
                picklistId: route.routeId
            };
        }

        return {
            operationType: PICK_STATUSES.includes(inProgressPicklist.status) ? UNASSIGN_PICKER : UNASSIGN_STAGER,
            routeId: route.routeId,
            picklistId: inProgressPicklist.picklistId
        };
    }

    const parseStationCode = (stationCode) => {
        if (stationCode && stationCode !== "NA") {
            return stationCode
        }
        return getStationCode();
    }

    const renderAssociateDetail = () => {
        let associateList = [];
        if (configViewModel.stationConfig.parallelPickEnabled || (isNodeARorSSD && viewModelRD.routeInfo)) {
            associateList = associateList.concat(viewModelRD.routeInfo.pickAssociatesAlias)
        } else if (props.route.associate !== "NA") {
            associateList.push(props.route.associate);
        }
        if (!associateList.length) {
            return <Text type="b300">...</Text>
        }

        return associateList.map(associate => (
            <Link key={associate} href={getFCLMLink(associate, configViewModel.stationConfig.fclmLinkEnabled, parseStationCode(props.route.stationCode))} type="secondary" target="_blank"
                  rel="noreferrer noopener">
                {associate}
            </Link>
        ))
    }
    
    const executeAction = (operation) => {
        switch (operation) {
            case "Sideline":
                dispatchRD({
                    type: 'SIDELINE_ROUTE',
                    data: {
                        dispatcher: dispatchRD,
                        routeId: props.route.routeId,
                        stationCode: parseStationCode(props.route.stationCode),
                        baseUrl: configViewModel.baseUrl,
                        alertDispatch: alertDispatch,
                        stationConfig: configViewModel.stationConfig
                    }
                })
                return;
            case "UnAssign":
                dispatchRD({
                    type: 'UNASSIGN_ASSOCIATE',
                    data: {
                        dispatcher: dispatchRD,
                        picklistId: viewModelRD.routeInfo.stageListId || getPicklistToUnassign(props.route, viewModelRD),
                        associateId: viewModelRD.routeInfo.picker,
                        stationCode: parseStationCode(props.route.stationCode),
                        baseUrl: configViewModel.baseUrl,
                        alertDispatch: alertDispatch,
                        stationConfig: configViewModel.stationConfig,
                        unassignParams: getUnassignParams(props.route, viewModelRD)
                    }
                });
                return;
            case "ForceAssign":
                setOpenForceAssignModal(true)
                return;
            case "PrioritizeRoute":
                dispatchRD({
                    type: 'PRIORITIZE_ROUTE',
                    data: {
                        dispatcher: dispatchRD,
                        routeId: props.route.routeId,
                        stationCode: parseStationCode(props.route.stationCode),
                        position: TOP_PRIORITY,
                        baseUrl: configViewModel.baseUrl,
                        alertDispatch: alertDispatch,
                        stationConfig: configViewModel.stationConfig
                    }
                });
                return;
            default:
                return;
        }
    }

    const getForceAssignConfirmationMessage = () => {
        return <Row alignmentVertical="top" alignmentHorizontal={"center"} spacingInset="none">
            <Column spacingInset={"small"} spacing={"small"}>
                <Text type="h200">
                    <FormattedMessage
                        id="associate.ForceAssignConfirmationLine1"
                        defaultMessage="The route stow progress is "/>
                    {viewModelRD.routeInfo ? viewModelRD.routeInfo.stowProgress : null}
                    <Text type="h200">
                        <FormattedMessage
                            id="associate.ForceAssignConfirmationLine2"
                            defaultMessage="Are you sure you want to Force Assign? "/>
                    </Text>

                </Text>
                <Text type="h200">
                    <FormattedMessage
                        id="associate.ForceAssignConfirmationLine3"
                        defaultMessage="Note: You will not be able to undo this action."/>
                </Text>
            </Column>
        </Row>
    }

    const onForceAssignConfirmation = () => {
        dispatchRD({
            type: 'FORCE_ASSIGN',
            data: {
                dispatcher: dispatchRD,
                routeId: props.route.routeId,
                stationCode: parseStationCode(props.route.stationCode),
                baseUrl: configViewModel.baseUrl,
                alertDispatch: alertDispatch,
                stationConfig: configViewModel.stationConfig
            }
        });
        setOpenForceAssignModal(false)
    }

    const onForceAssignCancel = () => {
        setOpenForceAssignModal(false);
    }

    function canRouteBeSidelined(viewModelRD) {
        return viewModelRD.routeInfo && !viewModelRD.routeInfo.sidelined &&
            SIDELINE_ENABLED_STATES.includes(viewModelRD.routeInfo.routeStatus) && !viewModelRD.isLoading
    }

    function canRouteBeForceAssigned(viewModelRD) {
        if (configViewModel && configViewModel.stationConfig
            && configViewModel.stationConfig.disableForceAssignForUnSplittedRoutes) {
            if (!isPicklistSplittingComplete(viewModelRD.routeInfo)) {
                return false;
            }
        }

        return viewModelRD.routeInfo && viewModelRD.routeInfo.routeStatus === NOT_READY &&
            !viewModelRD.routeInfo.sidelined && !viewModelRD.isLoading


    }

    const showForceAssignButton = () => {
        return (isAuthorizedUser && ((viewModelRD.routeInfo && viewModelRD.picklists
            && isReversePicklist(viewModelRD.routeInfo))
            || isNodeARorSSD || (viewModelRD.routeInfo && isDynamicRoute(viewModelRD.routeInfo.algorithm))));
    }

    useEffect( () => {
        if(isTCPAuthorizationCheckEnabled ){
            getFeaturesFromParent([FEATURES.IS_TCP_AUTHORIZATION_CHECK]).then(featureStatus => {
                isTCPAuthorizedUser(featureStatus.data[FEATURES.IS_TCP_AUTHORIZATION_CHECK]).then(
                    (isAuthorizedUser) => dispatchRD({
                        type: 'SET_USER_AUTHORIZATION',
                        data: {
                            isAuthorizedUser: isAuthorizedUser,
                            isTCPAuthorizationCheckEnabled: false
                        }
                    })
                )
            })

        }

    }, [])

    function canRouteBeUnassigned(viewModelRD) {
        return viewModelRD.routeInfo && viewModelRD.routeInfo.routeStatus === IN_PROGRESS &&
            viewModelRD.routeInfo.picker &&
            !viewModelRD.routeInfo.sidelined && !viewModelRD.isLoading

    }

    function canRouteBePrioritized(viewModelRD) {
        return viewModelRD.routeInfo && PRIORITIZE_ENABLED_STATES.includes(viewModelRD.routeInfo.routeStatus) &&
            !viewModelRD.routeInfo.sidelined;
    }

    function countNotInCartPackages(viewModelRD) {
        let cnt = 0;
        viewModelRD.containers.forEach(container => {
            if (container.name === NOT_IN_CART && container.packages) {
                cnt += container.packages.length;
            }
        });
        return cnt;
    }

    const renderMetadata = () => {
        return (
            <div>
                <Row widths={["grid-6"]} spacingInset="small none small">
                    <Heading level={3} type="h600" alignment="left">{props.route.code}</Heading>
                    {isNodeARorSSD ?
                        <Button data-id="Sideline" onClick={() => executeAction("Sideline")} type="tertiary"
                                size="medium"
                                disabled={!canRouteBeSidelined(viewModelRD)}>
                            <FormattedMessage id="routeInfo.sideline" defaultMessage="Sideline"/>
                        </Button> : null}
                    {(showForceAssignButton())?
                        <Button data-id="ForceAssign" onClick={() => executeAction("ForceAssign")} type="tertiary"
                                size="medium"
                                disabled={!canRouteBeForceAssigned(viewModelRD)}>
                            <FormattedMessage id="routeInfo.forceAssign" defaultMessage="Force Assign"/>
                        </Button> : null}
                    <CancelConfirmModal data ={{
                        innerHTML: getForceAssignConfirmationMessage(),
                        cancelHandler: onForceAssignCancel,
                        confirmHandler: onForceAssignConfirmation,
                        visible: openForceAssignModal
                    }}/>
                    {isNodeARorSSD ?
                        <Button data-id="UnAssign" onClick={() => executeAction("UnAssign")} type="tertiary"
                                size="medium" disabled={!canRouteBeUnassigned(viewModelRD)}>
                            <FormattedMessage id="routeInfo.unAssign" defaultMessage="Unassign"/>
                        </Button> : null
                    }
                    {configViewModel.capability === AR ?
                        <Button data-id="PrioritizeRoute" onClick={() => executeAction("PrioritizeRoute")}
                                type="tertiary"
                                size="medium" disabled={!canRouteBePrioritized(viewModelRD)}>
                            <FormattedMessage id="routeInfo.prioritizeRoute" defaultMessage="Prioritize staging"/>
                        </Button> : null}
                </Row>
                <Row widths={["grid-6", "grid-6"]} backgroundColor={"secondary"} spacing={"none"}
                     alignmentVertical={"stretch"}>
                    <Column spacingInset="medium" alignmentVertical={"top"} spacing={"small"}>
                        {/* Route Status */}
                        <Row width="100%" alignmentVertical="baseline">
                            <Text type="h100">
                                <FormattedMessage id="routeInfo.status" defaultMessage="Status"/>:
                            </Text>
                            <Text type="b300">
                                {configViewModel.stationConfig.parallelPickEnabled || (isNodeARorSSD && viewModelRD.routeInfo) ?
                                    showRouteStatus(viewModelRD.routeInfo, configViewModel.stationConfig) : parseURLParam(props.route.status)
                                }
                            </Text>
                        </Row>
                        {/* Picklist Count */}
                        {configViewModel.stationConfig.parallelPickEnabled || configViewModel.stationConfig.routeDetailsTabViewEnabled ?
                            <Row width="100%" alignmentVertical="baseline">
                                <Text type="h100">
                                    <FormattedMessage id="routeInfo.picklists" defaultMessage="Picklists"/>:
                                </Text>
                                <Text type="b300">
                                    {viewModelRD.picklists.length}
                                </Text>
                            </Row>
                            : null
                        }
                        {/* Staging Location */}
                        <Row width="100%" alignmentVertical="baseline">
                            <Text type="h100">
                                <FormattedMessage id="cartContent.location" defaultMessage="Staging location"/>:
                            </Text>
                            <Text type="b200">
                                {configViewModel.stationConfig.parallelPickEnabled || configViewModel.stationConfig.highSPREnabled || (isNodeARorSSD && viewModelRD.routeInfo) ?
                                    viewModelRD.routeInfo.stagingLocation
                                    : parseURLParam(props.route.location)
                                }
                            </Text>
                        </Row>
                        {/* Associate links */}
                        <Row width="100%" alignmentVertical="baseline">
                            <Text type="h100">
                                <FormattedMessage id="routeInfo.associate" defaultMessage="Associate"/>:
                            </Text>
                            <Row spacing={"small"}>{renderAssociateDetail()}</Row>

                        </Row>
                    </Column>
                    <Column spacingInset="medium" alignmentVertical={"top"} spacing={"small"}>
                        {/* Bag Count */}
                        <Row width="100%" alignmentVertical="baseline">
                            <Text type="h100">
                                <FormattedMessage id="pick.bags" defaultMessage="Bags"/>:
                            </Text>
                            <Text type="b300">
                                {props.route.status !== ASSIGNABLE ? viewModelRD.bagCount : "..."}
                            </Text>
                        </Row>
                        {/* OV Count */}
                        <Row width="100%" alignmentVertical="baseline">
                            <Text type="h100">
                                <FormattedMessage id="pick.ovs" defaultMessage="OVs"/>:
                            </Text>
                            <Text type="b300">
                                {props.route.status !== ASSIGNABLE ? viewModelRD.ovCount : "..."}
                            </Text>
                        </Row>
                        {/* Hazmat Weight */}
                        <Row width="100%" alignmentVertical="baseline">
                            <Text type="h100">
                                <FormattedMessage id="routeInfo.hazmatWeight" defaultMessage="Hazmat Weight"/>:
                            </Text>
                            <Text type="b300">
                                {viewModelRD.hazmatWeight !== '...' ? `${viewModelRD.hazmatWeight} kg` : "..."}
                            </Text>
                        </Row>
                        {/* Not Stowed */}
                        {configViewModel.stationConfig.routeDetailsTabViewEnabled ?
                            <Row width="100%" alignmentVertical="baseline">
                                <Text type="h100">
                                    <FormattedMessage id="routeInfo.notStowed" defaultMessage="Not Stowed"/>:
                                </Text>
                                <Text type="b300">
                                    {countNotInCartPackages(viewModelRD)}
                                </Text>
                            </Row>
                            : null}
                    </Column>
                </Row>
            </div>)
    }

    /* RENDER UI WHEN READY */
    if (viewModelRD.initialised && (!configViewModel.stationConfig.parallelPickEnabled || viewModelRD.routeInfo)) {
        return renderMetadata();
    }
    return null;
}

export default RouteMetadataV2


// WEBPACK FOOTER //
// ./src/components/routeDrillDown/RouteMetadataV2.js